import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, RadialGradient, Rect, Stop } from 'react-native-svg';

type Props = {
  width: number;
  height: number;
};

type Star = {
  cx: number;
  cy: number;
  r: number;
  opacity: number;
};

/**
 * Mulberry32 — a small, fast, well-distributed PRNG. We need a *deterministic*
 * starfield so the layout doesn't shimmer on every re-render, but we still want
 * a different look on each device size; seeding off (width|height) gives us
 * both properties without persisting any state.
 */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function rand(): number {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Nighttime wallpaper used by the `auto` theme. SVG starfield + galactic
 * gradient — no PNG asset needed. The star list is memoised against the
 * dimensions so resize/orientation changes regenerate, but normal re-renders
 * (e.g. theme refresh) reuse the same set and stay still on screen.
 */
export function CosmosWallpaper({ width, height }: Props) {
  const stars = useMemo<Star[]>(() => {
    // Density: ~1 star per 4000 px², capped so very large screens still render
    // in a single SVG node without choking on thousands of children.
    const target = Math.min(180, Math.round((width * height) / 4000));
    const seed = Math.max(1, Math.round(width * 73 + height * 31));
    const rand = mulberry32(seed);
    const out: Star[] = [];
    for (let i = 0; i < target; i += 1) {
      const sizeRoll = rand();
      const r = sizeRoll < 0.85 ? 0.7 + rand() * 0.6 : 1.3 + rand() * 1.2;
      out.push({
        cx: rand() * width,
        cy: rand() * height,
        r,
        opacity: 0.35 + rand() * 0.6,
      });
    }
    return out;
  }, [width, height]);

  const layout = useMemo(() => {
    const moonCx = width * 0.78;
    const moonCy = height * 0.18;
    const moonR = Math.min(width, height) * 0.07;
    return { moonCx, moonCy, moonR };
  }, [width, height]);

  return (
    <View style={[StyleSheet.absoluteFill, { width, height }]} pointerEvents="none">
      <Svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <Defs>
          <LinearGradient id="nightSky" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0%" stopColor="#020617" stopOpacity={1} />
            <Stop offset="55%" stopColor="#0f172a" stopOpacity={1} />
            <Stop offset="100%" stopColor="#1e1b4b" stopOpacity={1} />
          </LinearGradient>
          <RadialGradient
            id="milkyway"
            cx="50%"
            cy="62%"
            r="62%"
            fx="50%"
            fy="62%">
            <Stop offset="0%" stopColor="#312e81" stopOpacity={0.55} />
            <Stop offset="55%" stopColor="#1e293b" stopOpacity={0.25} />
            <Stop offset="100%" stopColor="#020617" stopOpacity={0} />
          </RadialGradient>
          <RadialGradient
            id="moonHalo"
            cx={String(layout.moonCx)}
            cy={String(layout.moonCy)}
            r={String(layout.moonR * 4)}
            fx={String(layout.moonCx)}
            fy={String(layout.moonCy)}
            gradientUnits="userSpaceOnUse">
            <Stop offset="0%" stopColor="#e0e7ff" stopOpacity={0.6} />
            <Stop offset="55%" stopColor="#a5b4fc" stopOpacity={0.18} />
            <Stop offset="100%" stopColor="#a5b4fc" stopOpacity={0} />
          </RadialGradient>
          <RadialGradient
            id="moonBody"
            cx={String(layout.moonCx)}
            cy={String(layout.moonCy)}
            r={String(layout.moonR)}
            fx={String(layout.moonCx - layout.moonR * 0.3)}
            fy={String(layout.moonCy - layout.moonR * 0.3)}
            gradientUnits="userSpaceOnUse">
            <Stop offset="0%" stopColor="#f8fafc" stopOpacity={1} />
            <Stop offset="100%" stopColor="#cbd5e1" stopOpacity={1} />
          </RadialGradient>
        </Defs>

        {/* Sky gradient */}
        <Rect x={0} y={0} width={width} height={height} fill="url(#nightSky)" />

        {/* Faint galactic band */}
        <Rect x={0} y={0} width={width} height={height} fill="url(#milkyway)" />

        {/* Starfield */}
        {stars.map((s, i) => (
          <Circle
            key={`s-${i}`}
            cx={s.cx}
            cy={s.cy}
            r={s.r}
            fill="#f8fafc"
            opacity={s.opacity}
          />
        ))}

        {/* Moon halo + disc */}
        <Circle
          cx={layout.moonCx}
          cy={layout.moonCy}
          r={layout.moonR * 4}
          fill="url(#moonHalo)"
        />
        <Circle
          cx={layout.moonCx}
          cy={layout.moonCy}
          r={layout.moonR}
          fill="url(#moonBody)"
        />
      </Svg>
    </View>
  );
}
